import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Collapse, Divider, Input, Row, Switch, Typography } from 'antd';
import { TbArrowRight, TbChevronRight, TbCloudUpload, TbFlask, TbWorld } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { workspaceService } from '@/services/database';
import { LearnMoreFooter } from '@/shared';
import { PlatformForm as Form } from '@/shared/components/forms';
import { useUserStore } from '@/stores';
import { AuditLogStreamDestination } from '@/types';

type DestinationField = {
    label: string;
    name: string;
    placeholder?: string;
    type?: 'password' | 'text';
};

type Destination = {
    description: string | ReactNode;
    fields: DestinationField[];
    key: string;
    name: string;
};

const destinations: Destination[] = [
    {
        description: 'Send signed audit events to a customer-managed HTTPS collector.',
        fields: [
            { label: 'Endpoint URL', name: 'endpointUrl', placeholder: 'https://logs.example.com/audit...' },
            { label: 'Signing Secret', name: 'signingSecret', placeholder: 'Shared HMAC Secret...', type: 'password' },
            { label: 'Header Name', name: 'headerName', placeholder: 'X-Countercyclical-Signature...' },
        ],
        key: 'generic-https',
        name: 'Generic HTTPS',
    },
];

function renderField(field: DestinationField) {
    if (field.type === 'password') {
        return <Input.Password placeholder={field.placeholder} />;
    }

    return <Input placeholder={field.placeholder} />;
}

interface AuditLogStreamingProps {
    workspaceId?: string;
}

const AuditLogStreaming: FC<AuditLogStreamingProps> = ({ workspaceId }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { selectedUser: user } = useUserStore();
    const activeWorkspaceId = workspaceId ?? user?.activeWorkspace?.id;
    const [enabledDestinations, setEnabledDestinations] = useState<Record<string, boolean>>({});
    const [streamDestinations, setStreamDestinations] = useState<AuditLogStreamDestination[]>([]);

    const handleLoadDestinations = useCallback(async () => {
        if (!activeWorkspaceId) {
            return;
        }

        try {
            const destinations = await workspaceService.getAuditLogStreamDestinations(activeWorkspaceId);

            setStreamDestinations(destinations);
            setEnabledDestinations(
                destinations.reduce<Record<string, boolean>>((accumulator, destination) => {
                    accumulator[destination.provider] = destination.enabled;

                    return accumulator;
                }, {}),
            );
        } catch (error) {
            captureException(error);
        }
    }, [activeWorkspaceId]);

    useEffect(() => {
        handleLoadDestinations();
    }, [handleLoadDestinations]);

    const header = (destination: Destination): ReactNode => (
        <Row align='middle' gutter={12} wrap={false}>
            <Col flex='none'>
                <Avatar
                    icon={
                        <TbWorld size='1.25rem' style={{ verticalAlign: '-0.2rem', color: token.colorTextTertiary }} />
                    }
                    shape='square'
                    style={{ border: `1px solid ${token.colorBorder}` }}
                />
            </Col>
            <Col flex='auto'>
                <strong>{destination.name}</strong>
                <br />
                <small style={{ color: token.colorTextTertiary }}>{destination.description}</small>
            </Col>
            <Col flex='none' onClick={(event) => event.stopPropagation()}>
                <Switch
                    checked={Boolean(enabledDestinations[destination.key])}
                    onChange={async (checked) => {
                        setEnabledDestinations((previous) => ({ ...previous, [destination.key]: checked }));

                        const savedDestination = streamDestinations.find((item) => item.provider === destination.key);

                        if (!activeWorkspaceId || !savedDestination) {
                            return;
                        }

                        try {
                            const updatedDestination = await workspaceService.setAuditLogStreamDestination(
                                activeWorkspaceId,
                                destination.key,
                                {
                                    config: savedDestination.config ?? {},
                                    enabled: checked,
                                    name: destination.name,
                                },
                            );

                            setStreamDestinations((previous) =>
                                previous.map((item) => (item.provider === destination.key ? updatedDestination : item)),
                            );
                        } catch (error) {
                            captureException(error);
                            toast.error(`Could not update ${destination.name}`);
                        }
                    }}
                />
            </Col>
        </Row>
    );

    return (
        <>
            <Row align='top' wrap={false} style={{ marginBottom: '1rem' }}>
                <Col flex='auto'>
                    <Typography.Title level={4} style={{ marginBottom: '0.25rem' }}>
                        <TbCloudUpload style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }} />
                        Event Streaming
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                        Configure a Generic HTTPS destination for continuous audit log export.
                    </Typography.Paragraph>
                </Col>
            </Row>
            <Collapse
                defaultActiveKey={['']}
                accordion
                expandIcon={({ isActive }) => (
                    <TbChevronRight
                        size='1rem'
                        style={{
                            color: token.colorTextTertiary,
                            transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                            verticalAlign: '-0.175rem',
                        }}
                    />
                )}
            >
                {destinations.map((destination) => (
                    <Collapse.Panel header={header(destination)} key={destination.key}>
                        <Form
                            layout='vertical'
                            initialValues={
                                streamDestinations.find((item) => item.provider === destination.key)?.config ?? {}
                            }
                            onFinish={async (values) => {
                                if (!activeWorkspaceId) {
                                    return;
                                }

                                try {
                                    const savedDestination = await workspaceService.setAuditLogStreamDestination(
                                        activeWorkspaceId,
                                        destination.key,
                                        {
                                            config: values,
                                            enabled: true,
                                            name: destination.name,
                                        },
                                    );

                                    setStreamDestinations((previous) => {
                                        const existingDestination = previous.some(
                                            (item) => item.provider === savedDestination.provider,
                                        );

                                        if (existingDestination) {
                                            return previous.map((item) =>
                                                item.provider === savedDestination.provider ? savedDestination : item,
                                            );
                                        }

                                        return [...previous, savedDestination];
                                    });
                                    setEnabledDestinations((previous) => ({ ...previous, [destination.key]: true }));
                                    toast(`${destination.name} connection saved`);
                                } catch (error) {
                                    captureException(error);
                                    toast.error(`Could not connect ${destination.name}`);
                                }
                            }}
                        >
                            <Row gutter={[16, 0]}>
                                {destination.fields.map((field) => (
                                    <Col xs={24} md={12} key={field.name}>
                                        <Form.Item
                                            label={field.label}
                                            name={field.name}
                                            style={{ marginBottom: '0.5rem' }}
                                        >
                                            {renderField(field)}
                                        </Form.Item>
                                    </Col>
                                ))}
                            </Row>
                            <Row justify='end' gutter={8} align='middle'>
                                <Col>
                                    <Button icon={<TbFlask size='1rem' style={{ verticalAlign: '-0.15rem' }} />}>
                                        Test
                                    </Button>
                                </Col>
                                <Col>
                                    <Divider type='vertical' style={{ margin: 0 }} />
                                </Col>
                                <Col>
                                    <Button htmlType='submit' type='primary'>
                                        Connect
                                        <TbArrowRight size='1rem' style={{ verticalAlign: '-0.15rem' }} />
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Collapse.Panel>
                ))}
            </Collapse>
            <Divider />
            <LearnMoreFooter
                align='left'
                message={
                    <>
                        Events should be delivered as JSON with the audit log's id, action, resource, actor, workspace,
                        IP, device metadata, timestamp, and raw details. Learn more about audit logs in
                    </>
                }
            />
        </>
    );
};

export default AuditLogStreaming;
